/**
 * @module immersive/StagePage
 * @description One cause's Stage (vision §11): cover marquee, name + tags,
 * Love Meter, story, the (simulated) love-stream overlay, and the Quiver bar
 * with Legend Arrows. A tap = a Shot: the arrow's US$ value is added to this
 * cause in the Quiver, and the donor's own screen plays the Cupid celebration.
 */
import { useMemo, useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate, routeParam } from "../app/router";
import { CATEGORY_LABELS, PARISH_LABELS } from "../config/taxonomy";
import { arrowLabel, availableArrows, breakdown, groupArrows, LEGEND_ARROWS, type LegendArrow } from "../domain/arrows";
import type { Cause, CartLine } from "../domain/types";
import { formatUsd } from "../lib/format";
import { Icon } from "../components/Icons";
import { ArrowFace, ArrowGlyph } from "./ArrowFace";
import { ActivityBubble, LoveMeter } from "./Lobby";
import { useStage, type StreamEvent } from "./StageContext";

/** Live love-stream overlay for one stage (simulated, labeled). */
function LoveStream({ causeId }: { causeId: string }): JSX.Element {
    const { stream } = useStage();
    const events: StreamEvent[] = stream.filter((e: StreamEvent) => e.causeId === causeId).slice(0, 5);
    return (
        <aside className="love-stream" aria-label="Love-stream (simulated)" aria-live="polite">
            <p className="love-stream__head"><span className="live-dot" /> Love-stream <span className="stage-flag">Simulated</span></p>
            {events.length === 0 && <p className="love-stream__empty">Waiting for the next gift…</p>}
            <ul className="love-stream__list">
                {events.map((e: StreamEvent) => (
                    <li key={e.id} className="love-stream__item">
                        <span className="love-stream__arrow"><ArrowGlyph size={20} twin={e.arrow.legends.length > 1} /></span>
                        <span><strong>{e.displayName}</strong> sent a {e.arrow.name} arrow{e.comment && <span className="love-stream__comment"> “{e.comment}”</span>}</span>
                    </li>
                ))}
            </ul>
        </aside>
    );
}

/** The stage page. */
export function StagePage(): JSX.Element {
    const { causeById, causes, lines, dispatchCart, setCartOpen } = useGiving();
    const { celebrate, rebelPreview, setRebelPreview } = useStage();
    const [custom, setCustom] = useState<string>("");
    const cause: Cause | undefined = causeById(routeParam() ?? "");
    const arrows: LegendArrow[] = useMemo(() => availableArrows(rebelPreview), [rebelPreview]);

    if (causes === null) return <main className="stage-page"><div className="spinner" /></main>;
    if (cause === undefined) {
        return (
            <main className="stage-page container stage-missing">
                <h1>This stage isn't here</h1>
                <button type="button" className="btn btn--gold" onClick={() => navigate("browse")}>Back to the lobby</button>
            </main>
        );
    }

    const line: CartLine | undefined = lines.find((l: CartLine) => l.causeId === cause.id);
    const inQuiver: [LegendArrow, number][] = line ? groupArrows(breakdown(line.amountUsd, arrows)) : [];

    /** One Shot: add value to this cause, celebrate on the donor's screen only. */
    const shoot = (shot: LegendArrow[]): void => {
        const total: number = shot.reduce((s: number, a: LegendArrow) => s + a.faceUsd, 0);
        if (total < 1) return;
        dispatchCart({ type: "increment", causeId: cause.id, amountUsd: total });
        celebrate(shot, cause);
    };
    const customAmount: number = Number.parseInt(custom, 10);
    const customArrows: LegendArrow[] = Number.isFinite(customAmount) && customAmount >= 1 ? breakdown(customAmount, arrows) : [];
    const rebel: LegendArrow | undefined = LEGEND_ARROWS.find((a: LegendArrow) => a.availability === "rebel");

    return (
        <main className="stage-page">
            <section className="stage-cover">
                <img className="stage-cover__img" src={cause.image.src} alt={cause.image.alt} />
                <div className="stage-cover__shade" />
                <div className="container stage-cover__inner">
                    <button type="button" className="stage-back" onClick={() => navigate("browse")}><Icon name="chevron-left" size={16} /> Lobby</button>
                    <div className="stage-cover__meta">
                        <ActivityBubble causeId={cause.id} />
                        <span className="stage-chip stage-chip--static">{CATEGORY_LABELS[cause.category]}</span>
                        <span className="stage-chip stage-chip--static">{PARISH_LABELS[cause.parish]}</span>
                    </div>
                    <p className="stage-cover__partner">{cause.partner}</p>
                    <h1 className="stage-cover__title">{cause.title}</h1>
                    <LoveMeter cause={cause} />
                </div>
                <LoveStream causeId={cause.id} />
            </section>

            <div className="container stage-body">
                <section className="stage-story" aria-labelledby="story-title">
                    <h2 id="story-title" className="stage-h2">The story</h2>
                    <p>{cause.summary}</p>
                    <p className="stage-impact"><strong>{cause.impact.value}</strong> {cause.impact.label} <span className="stage-muted">· AFJ-reported impact</span></p>
                    <p className="stage-muted">Gifts are tax-deductible gifts to The American Friends of Jamaica, a 501(c)(3), designated to {cause.partner}.</p>
                </section>

                <section className="quiver-bar" aria-labelledby="arrows-title">
                    <div className="quiver-bar__head">
                        <h2 id="arrows-title" className="stage-h2">Shoot a Legend Arrow</h2>
                        <p className="stage-muted">You give in US dollars. Legends are named after the Jamaican note or coin with the same number.</p>
                    </div>
                    <div className="arrow-grid">
                        {arrows.map((a: LegendArrow) => (
                            <button key={a.id} type="button" className="arrow-btn" onClick={() => shoot([a])} aria-label={`Shoot ${arrowLabel(a)}`} title={`${a.legends.join(" and ")} · ${a.source}`}>
                                <ArrowFace arrow={a} />
                            </button>
                        ))}
                        {!rebelPreview && rebel && (
                            <div className="arrow-btn arrow-btn--locked" aria-label="Rebel Arrow, locked">
                                <ArrowFace arrow={rebel} />
                                <span className="arrow-btn__lock">Earned by monthly donors</span>
                            </div>
                        )}
                    </div>
                    <label className="rebel-toggle">
                        <input type="checkbox" checked={rebelPreview} onChange={(e) => setRebelPreview(e.target.checked)} />
                        <span>Demo: preview as a monthly donor (unlocks the US$2 Bogle Rebel Arrow)</span>
                    </label>

                    <div className="custom-shot">
                        <label className="custom-shot__field">
                            <span>Any amount</span>
                            <span className="custom-shot__input">US$<input type="number" inputMode="numeric" min={1} step={1} value={custom} placeholder="27" onChange={(e) => setCustom(e.target.value)} /></span>
                        </label>
                        {customArrows.length > 0 && (
                            <p className="custom-shot__preview">
                                Fewest arrows: {groupArrows(customArrows).map(([a, n]) => `${a.name}${n > 1 ? ` ×${n}` : ""}`).join(" + ")}
                            </p>
                        )}
                        <button type="button" className="btn btn--gold" disabled={customArrows.length === 0} onClick={() => { shoot(customArrows); setCustom(""); }}>
                            Shoot {customArrows.length > 0 ? formatUsd(customAmount) : ""}
                        </button>
                    </div>

                    {line && (
                        <div className="quiver-summary">
                            <p><strong>In your Quiver for this stage:</strong> {formatUsd(line.amountUsd)}</p>
                            <div className="quiver-summary__arrows">{inQuiver.map(([a, n]) => <ArrowFace key={a.id} arrow={a} count={n} />)}</div>
                            <button type="button" className="btn btn--on-dark" onClick={() => setCartOpen(true)}>Open your Quiver</button>
                        </div>
                    )}
                </section>
            </div>
        </main>
    );
}
